import React, { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { useAdmin } from "../context/AdminContext";
import {
  LayoutDashboard, ShoppingBag, Package, LogOut, TrendingUp, AlertTriangle,
  IndianRupee, CheckCircle2, RefreshCw, Plus, Trash2, X, Edit3, Image as ImageIcon,
  Layers, Settings as SettingsIcon, Instagram, Ticket, Gift, Coins,
} from "lucide-react";
import { toast } from "sonner";
import { SettingsTab, SubcategoriesTab, UGCTab, CouponsTab, SpinTab, LoyaltyTab } from "./AdminMore";
import ImageUploader from "../components/ImageUploader";

const STATUS_OPTIONS = ["created", "paid", "shipped", "delivered", "failed", "refunded", "refund_initiated", "disputed"];
const STATUS_PILL = {
  created: "bg-amber-100 text-amber-800", paid: "bg-green-100 text-green-800",
  shipped: "bg-blue-100 text-blue-800", delivered: "bg-green-100 text-green-800",
  failed: "bg-red-100 text-red-800", refunded: "bg-neutral-200 text-neutral-700",
  refund_initiated: "bg-amber-100 text-amber-800", disputed: "bg-red-100 text-red-800",
  signature_failed: "bg-red-100 text-red-800",
};
const LOCATIONS = ["hero", "promo", "strip"];

export default function Admin() {
  const { authed, checking, logout, api } = useAdmin();
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [banners, setBanners] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [ugc, setUgc] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [prizes, setPrizes] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [s, o, p, c, b, sub, u, cp, pz, st] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/orders"),
        api.get("/admin/products"),
        api.get("/admin/categories"),
        api.get("/admin/banners"),
        api.get("/admin/subcategories"),
        api.get("/admin/ugc"),
        api.get("/admin/coupons"),
        api.get("/admin/spin-prizes"),
        api.get("/site-settings"),
      ]);
      setStats(s.data); setOrders(o.data); setProducts(p.data); setCategories(c.data); setBanners(b.data);
      setSubcategories(sub.data); setUgc(u.data); setCoupons(cp.data); setPrizes(pz.data); setSettings(st.data);
    } catch (e) {
      toast.error("Failed to load admin data", { description: e?.response?.data?.detail || e.message });
    } finally { setLoading(false); }
  };

  useEffect(() => { if (authed) fetchAll(); /* eslint-disable-next-line */ }, [authed]);

  if (checking) return null;
  if (!authed) return <Navigate to="/admin/login" replace />;

  return (
    <main className="max-w-[1400px] mx-auto px-4 md:px-8 py-8 md:py-12" data-testid="admin-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl md:text-4xl font-black">Admin Dashboard</h1>
          <p className="text-neutral-500 text-sm">Manage Stealodeal orders, catalog & banners</p>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/" className="h-10 px-4 rounded-full border border-neutral-300 text-sm font-semibold hover:bg-neutral-100">View Store</Link>
          <button onClick={fetchAll} className="h-10 px-4 rounded-full border border-neutral-300 text-sm flex items-center gap-2 hover:bg-neutral-100" data-testid="admin-refresh-btn">
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
          <button onClick={logout} className="h-10 px-4 rounded-full bg-neutral-900 text-white text-sm font-semibold flex items-center gap-2" data-testid="admin-logout-btn">
            <LogOut className="w-4 h-4" /> Logout
          </button>
        </div>
      </div>

      <div className="mt-6 flex gap-1 bg-neutral-100 p-1 rounded-full w-fit flex-wrap">
        {[
          { v: "overview", label: "Overview", icon: LayoutDashboard },
          { v: "orders", label: "Orders", icon: ShoppingBag },
          { v: "products", label: "Products", icon: Package },
          { v: "categories", label: "Categories", icon: Layers },
          { v: "subcategories", label: "Sub-Cats", icon: Layers },
          { v: "banners", label: "Banners", icon: ImageIcon },
          { v: "ugc", label: "Instagram", icon: Instagram },
          { v: "coupons", label: "Coupons", icon: Ticket },
          { v: "spin", label: "Spin Wheel", icon: Gift },
          { v: "loyalty", label: "Loyalty", icon: Coins },
          { v: "settings", label: "Settings", icon: SettingsIcon },
        ].map((t) => (
          <button key={t.v} onClick={() => setTab(t.v)}
            className={`px-4 h-10 rounded-full text-sm font-semibold flex items-center gap-2 transition ${tab === t.v ? "bg-white shadow text-neutral-900" : "text-neutral-600"}`}
            data-testid={`admin-tab-${t.v}`}>
            <t.icon className="w-4 h-4" /> {t.label}
          </button>
        ))}
      </div>

      <div className="mt-8">
        {tab === "overview" && <Overview stats={stats} />}
        {tab === "orders" && <OrdersTable orders={orders} api={api} onChange={fetchAll} />}
        {tab === "products" && <ProductsTab products={products} categories={categories} api={api} onChange={fetchAll} />}
        {tab === "categories" && <CategoriesTab categories={categories} api={api} onChange={fetchAll} />}
        {tab === "subcategories" && <SubcategoriesTab subcategories={subcategories} categories={categories} api={api} onChange={fetchAll} />}
        {tab === "banners" && <BannersTab banners={banners} api={api} onChange={fetchAll} />}
        {tab === "ugc" && <UGCTab ugc={ugc} api={api} onChange={fetchAll} />}
        {tab === "coupons" && <CouponsTab coupons={coupons} api={api} onChange={fetchAll} />}
        {tab === "spin" && <SpinTab prizes={prizes} coupons={coupons} api={api} onChange={fetchAll} />}
        {tab === "loyalty" && <LoyaltyTab api={api} />}
        {tab === "settings" && <SettingsTab settings={settings} api={api} onChange={fetchAll} />}
      </div>
    </main>
  );
}

// ============ Overview ============
function Overview({ stats }) {
  if (!stats) return <div className="text-neutral-500">Loading...</div>;
  const cards = [
    { icon: IndianRupee, label: "Revenue (paid)", value: `₹${stats.paid_revenue.toLocaleString("en-IN")}`, color: "text-green-700", bg: "bg-green-50" },
    { icon: CheckCircle2, label: "Paid Orders", value: stats.paid_orders, color: "text-green-700", bg: "bg-green-50" },
    { icon: TrendingUp, label: "Total Orders", value: stats.total_orders, color: "text-neutral-900", bg: "bg-neutral-100" },
    { icon: AlertTriangle, label: "Low Stock SKUs", value: stats.low_stock_count, color: "text-amber-700", bg: "bg-amber-50" },
  ];
  return (
    <div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="admin-overview">
        {cards.map((c) => (
          <div key={c.label} className={`${c.bg} rounded-2xl p-5`}>
            <div className={`w-10 h-10 rounded-full bg-white flex items-center justify-center ${c.color}`}><c.icon className="w-5 h-5" /></div>
            <div className="mt-4 text-xs text-neutral-500 uppercase tracking-wider font-semibold">{c.label}</div>
            <div className="mt-1 font-display text-2xl md:text-3xl font-black">{c.value}</div>
          </div>
        ))}
      </div>
      <div className="mt-8 bg-white border border-neutral-200 rounded-3xl p-6">
        <h3 className="font-display text-xl font-black">Orders by status</h3>
        {stats.by_status.length === 0 ? (
          <div className="mt-4 text-neutral-500 text-sm">No orders yet.</div>
        ) : (
          <div className="mt-4 space-y-2">
            {stats.by_status.map((b) => (
              <div key={b.status} className="flex items-center justify-between py-2 border-b border-neutral-100 last:border-0">
                <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${STATUS_PILL[b.status] || "bg-neutral-100"}`}>{b.status}</span>
                <div className="text-sm"><span className="font-bold">{b.count}</span> orders · <span className="text-neutral-500">₹{b.revenue?.toLocaleString("en-IN") || 0}</span></div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ============ Orders ============
function OrdersTable({ orders, api, onChange }) {
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});

  const openEdit = (o) => {
    setEditing(o.id);
    setForm({ status: o.status || "", tracking_number: o.tracking_number || "", courier: o.courier || "", admin_notes: o.admin_notes || "" });
  };
  const save = async () => {
    try {
      await api.patch(`/admin/orders/${editing}`, form);
      toast.success("Order updated");
      setEditing(null); onChange();
    } catch (e) { toast.error("Update failed", { description: e?.response?.data?.detail || e.message }); }
  };

  return (
    <div className="bg-white border border-neutral-200 rounded-3xl overflow-hidden" data-testid="admin-orders-table">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-neutral-50 text-xs uppercase tracking-wider text-neutral-500">
            <tr>
              <th className="text-left p-4">Order</th><th className="text-left p-4">Customer</th>
              <th className="text-left p-4">Status</th><th className="text-right p-4">Amount</th>
              <th className="text-left p-4">Created</th><th className="text-right p-4">Action</th>
            </tr>
          </thead>
          <tbody>
            {orders.length === 0 && <tr><td colSpan={6} className="text-center py-10 text-neutral-500">No orders yet.</td></tr>}
            {orders.map((o) => (
              <tr key={o.id} className="border-t border-neutral-100" data-testid={`admin-order-${o.id}`}>
                <td className="p-4">
                  <Link to={`/order/${o.id}`} target="_blank" className="font-mono text-xs hover:text-[#FF5A1F]">{o.razorpay_order_id}</Link>
                  <div className="text-xs text-neutral-500 mt-0.5">{o.items?.length || 0} items</div>
                </td>
                <td className="p-4">
                  <div className="font-medium">{o.customer_name || "—"}</div>
                  <div className="text-xs text-neutral-500">{o.customer_email}</div>
                  <div className="text-xs text-neutral-500">{o.customer_phone}</div>
                </td>
                <td className="p-4">
                  <span className={`text-[11px] font-bold px-2.5 py-1 rounded-full ${STATUS_PILL[o.status] || "bg-neutral-100"}`}>{o.status}</span>
                  {o.tracking_number && <div className="text-xs text-neutral-500 mt-1">📦 {o.courier} · {o.tracking_number}</div>}
                </td>
                <td className="p-4 text-right font-bold">₹{o.amount_inr}</td>
                <td className="p-4 text-xs text-neutral-500">{new Date(o.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}</td>
                <td className="p-4 text-right">
                  <button onClick={() => openEdit(o)} className="text-xs font-semibold text-[#FF5A1F] hover:underline" data-testid={`edit-order-${o.id}`}>Edit</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <Modal onClose={() => setEditing(null)} title="Edit Order">
          <Field label="Status">
            <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="input" data-testid="edit-status-select">
              {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </Field>
          <Field label="Courier"><input value={form.courier} onChange={(e) => setForm({ ...form, courier: e.target.value })} placeholder="e.g. Delhivery" className="input" /></Field>
          <Field label="Tracking Number"><input value={form.tracking_number} onChange={(e) => setForm({ ...form, tracking_number: e.target.value })} className="input" /></Field>
          <Field label="Admin notes"><textarea value={form.admin_notes} onChange={(e) => setForm({ ...form, admin_notes: e.target.value })} rows={3} className="input resize-none" /></Field>
          <div className="mt-4 flex gap-2 justify-end">
            <button onClick={() => setEditing(null)} className="btn-secondary">Cancel</button>
            <button onClick={save} className="btn-primary" data-testid="save-order-btn">Save</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ============ Products ============
const emptyProduct = {
  slug: "", title: "", subtitle: "", brand: "Stealodeal", category: "",
  mrp: 0, sale: 0, rating: 4.5, reviews: "New", badge: "",
  color: "", material: "", about_item: [], description: "", key_features: [],
  images: [], amazon_url: "", stock: 0, active: true,
};

function ProductsTab({ products, categories, api, onChange }) {
  const { token } = useAdmin();
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(emptyProduct);
  const [saving, setSaving] = useState(false);

  const openCreate = () => {
    setForm({ ...emptyProduct, category: categories[0]?.slug || "" });
    setCreating(true); setEditing(null);
  };
  const openEdit = (p) => {
    setForm({ ...emptyProduct, ...p });
    setEditing(p.product_id); setCreating(false);
  };
  const close = () => { setEditing(null); setCreating(false); };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form,
        about_item: Array.isArray(form.about_item) ? form.about_item : String(form.about_item || "").split("\n").filter(Boolean),
        key_features: Array.isArray(form.key_features) ? form.key_features : String(form.key_features || "").split(",").map((s) => s.trim()).filter(Boolean),
        images: Array.isArray(form.images) ? form.images : String(form.images || "").split("\n").map((s) => s.trim()).filter(Boolean),
        mrp: Number(form.mrp), sale: Number(form.sale), stock: Number(form.stock), rating: Number(form.rating),
      };
      if (creating) {
        await api.post("/admin/products", payload);
        toast.success("Product created");
      } else {
        await api.patch(`/admin/products/${editing}`, payload);
        toast.success("Product updated");
      }
      close(); onChange();
    } catch (e) { toast.error("Save failed", { description: e?.response?.data?.detail || e.message }); }
    finally { setSaving(false); }
  };

  const del = async (p) => {
    if (!window.confirm(`Delete "${p.title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/products/${p.product_id}`);
      toast.success("Product deleted");
      onChange();
    } catch (e) { toast.error("Delete failed", { description: e?.response?.data?.detail || e.message }); }
  };

  return (
    <div data-testid="admin-products-tab">
      <div className="flex justify-between items-center mb-4">
        <div className="text-sm text-neutral-500">{products.length} product{products.length !== 1 ? "s" : ""}</div>
        <button onClick={openCreate} className="btn-primary flex items-center gap-2" data-testid="add-product-btn">
          <Plus className="w-4 h-4" /> Add Product
        </button>
      </div>

      <div className="bg-white border border-neutral-200 rounded-3xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 text-xs uppercase tracking-wider text-neutral-500">
              <tr>
                <th className="text-left p-4">Product</th><th className="text-left p-4">Category</th>
                <th className="text-right p-4">MRP</th><th className="text-right p-4">Sale ₹</th>
                <th className="text-right p-4">Stock</th><th className="text-center p-4">Active</th>
                <th className="text-right p-4 pr-6">Actions</th>
              </tr>
            </thead>
            <tbody>
              {products.map((p) => (
                <tr key={p.product_id} className="border-t border-neutral-100" data-testid={`admin-product-${p.product_id}`}>
                  <td className="p-4">
                    <div className="flex items-center gap-3">
                      {p.images?.[0] && <div className="w-12 h-12 rounded-lg bg-neutral-100 overflow-hidden flex-shrink-0"><img src={p.images[0]} alt="" className="w-full h-full object-contain p-1" /></div>}
                      <div className="min-w-0">
                        <div className="font-medium text-neutral-900 line-clamp-2 max-w-md">{p.title}</div>
                        <div className="text-xs text-neutral-500 font-mono">{p.slug}</div>
                      </div>
                    </div>
                  </td>
                  <td className="p-4 text-xs">{p.category}</td>
                  <td className="p-4 text-right">₹{p.mrp}</td>
                  <td className="p-4 text-right font-semibold">₹{p.sale}</td>
                  <td className={`p-4 text-right ${p.stock <= 10 ? "text-amber-600 font-bold" : ""}`}>{p.stock}</td>
                  <td className="p-4 text-center">
                    <span className={`inline-block w-2 h-2 rounded-full ${p.active ? "bg-green-500" : "bg-neutral-300"}`} />
                  </td>
                  <td className="p-4 pr-6 text-right">
                    <div className="inline-flex gap-2">
                      <button onClick={() => openEdit(p)} className="text-xs font-semibold text-[#FF5A1F] hover:underline flex items-center gap-1" data-testid={`edit-product-${p.product_id}`}>
                        <Edit3 className="w-3.5 h-3.5" /> Edit
                      </button>
                      <button onClick={() => del(p)} className="text-xs font-semibold text-red-600 hover:underline flex items-center gap-1" data-testid={`delete-product-${p.product_id}`}>
                        <Trash2 className="w-3.5 h-3.5" /> Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {(creating || editing) && (
        <Modal onClose={close} title={creating ? "Add Product" : "Edit Product"} wide>
          <div className="grid md:grid-cols-2 gap-3">
            <Field label="Title *"><input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="input" data-testid="pf-title" /></Field>
            <Field label="Slug (URL) *"><input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} placeholder="e.g. black-rfid-wallet" className="input" data-testid="pf-slug" /></Field>
            <Field label="Subtitle"><input value={form.subtitle} onChange={(e) => setForm({ ...form, subtitle: e.target.value })} className="input" /></Field>
            <Field label="Brand"><input value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} className="input" /></Field>
            <Field label="Category *">
              <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="input" data-testid="pf-category">
                <option value="">— select —</option>
                {categories.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
              </select>
            </Field>
            <Field label="Badge (optional)"><input value={form.badge} onChange={(e) => setForm({ ...form, badge: e.target.value })} placeholder="Bestseller / New / Top Rated" className="input" /></Field>
            <Field label="MRP (₹)"><input type="number" value={form.mrp} onChange={(e) => setForm({ ...form, mrp: e.target.value })} className="input" data-testid="pf-mrp" /></Field>
            <Field label="Sale price (₹) *"><input type="number" value={form.sale} onChange={(e) => setForm({ ...form, sale: e.target.value })} className="input" data-testid="pf-sale" /></Field>
            <Field label="Stock"><input type="number" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} className="input" data-testid="pf-stock" /></Field>
            <Field label="Rating (0-5)"><input type="number" step="0.1" value={form.rating} onChange={(e) => setForm({ ...form, rating: e.target.value })} className="input" /></Field>
            <Field label="Reviews label"><input value={form.reviews} onChange={(e) => setForm({ ...form, reviews: e.target.value })} placeholder="e.g. 150+" className="input" /></Field>
            <Field label="Colour"><input value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} className="input" /></Field>
            <Field label="Material"><input value={form.material} onChange={(e) => setForm({ ...form, material: e.target.value })} className="input" /></Field>
            <Field label="Amazon URL"><input value={form.amazon_url} onChange={(e) => setForm({ ...form, amazon_url: e.target.value })} className="input" /></Field>
            <Field label="Product Video URL (YouTube / Vimeo)"><input value={form.video_url || ""} onChange={(e) => setForm({ ...form, video_url: e.target.value })} placeholder="https://youtu.be/..." className="input font-mono text-xs" data-testid="pf-video" /></Field>
          </div>

          <Field label="Description">
            <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} className="input resize-none" />
          </Field>

          <Field label="About this item (one bullet per line)">
            <textarea value={Array.isArray(form.about_item) ? form.about_item.join("\n") : form.about_item}
              onChange={(e) => setForm({ ...form, about_item: e.target.value.split("\n") })}
              rows={5} className="input resize-none" placeholder={"Bullet 1\nBullet 2\nBullet 3"} />
          </Field>

          <Field label="Key features (comma-separated)">
            <input value={Array.isArray(form.key_features) ? form.key_features.join(", ") : form.key_features}
              onChange={(e) => setForm({ ...form, key_features: e.target.value.split(",").map((s) => s.trim()) })}
              className="input" placeholder="RFID blocking, 6 slots, Stainless steel" />
          </Field>

          <Field label="Image URLs (one per line)">
            <textarea value={Array.isArray(form.images) ? form.images.join("\n") : form.images}
              onChange={(e) => setForm({ ...form, images: e.target.value.split("\n") })}
              rows={5} className="input resize-none font-mono text-xs" placeholder={"https://.../img1.jpg\nhttps://.../img2.jpg"} data-testid="pf-images" />
            <div className="mt-2">
              <ImageUploader token={token} label="Upload image" onUploaded={(url) => {
                const current = Array.isArray(form.images) ? form.images : String(form.images || "").split("\n").filter(Boolean);
                setForm({ ...form, images: [...current, url] });
              }} />
            </div>
          </Field>

          <label className="flex items-center gap-2 mt-4">
            <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="accent-[#FF5A1F] w-4 h-4" />
            <span className="text-sm font-medium">Active (show on storefront)</span>
          </label>

          <div className="mt-6 flex gap-2 justify-end">
            <button onClick={close} className="btn-secondary">Cancel</button>
            <button onClick={save} disabled={saving} className="btn-primary" data-testid="save-product-btn">
              {saving ? "Saving..." : creating ? "Create" : "Update"}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ============ Categories ============
const emptyCategory = { slug: "", name: "", blurb: "", image: "", order: 100, active: true };

function CategoriesTab({ categories, api, onChange }) {
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(emptyCategory);

  const openCreate = () => { setForm(emptyCategory); setCreating(true); setEditing(null); };
  const openEdit = (c) => { setForm({ ...c }); setEditing(c.slug); setCreating(false); };
  const close = () => { setCreating(false); setEditing(null); };

  const save = async () => {
    try {
      if (creating) {
        await api.post("/admin/categories", { ...form, order: Number(form.order) || 100 });
        toast.success("Category created");
      } else {
        const { slug: _slug, ...upd } = form;
        await api.patch(`/admin/categories/${editing}`, { ...upd, order: Number(upd.order) || 100 });
        toast.success("Category updated");
      }
      close(); onChange();
    } catch (e) { toast.error("Save failed", { description: e?.response?.data?.detail || e.message }); }
  };

  const del = async (c) => {
    if (!window.confirm(`Delete category "${c.name}"?`)) return;
    try { await api.delete(`/admin/categories/${c.slug}`); toast.success("Deleted"); onChange(); }
    catch (e) { toast.error("Delete failed", { description: e?.response?.data?.detail || e.message }); }
  };

  return (
    <div data-testid="admin-categories-tab">
      <div className="flex justify-between items-center mb-4">
        <div className="text-sm text-neutral-500">{categories.length} categor{categories.length !== 1 ? "ies" : "y"}</div>
        <button onClick={openCreate} className="btn-primary flex items-center gap-2" data-testid="add-category-btn">
          <Plus className="w-4 h-4" /> Add Category
        </button>
      </div>
      <div className="bg-white border border-neutral-200 rounded-3xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-neutral-50 text-xs uppercase tracking-wider text-neutral-500">
            <tr>
              <th className="text-left p-4">Category</th><th className="text-left p-4">Slug</th>
              <th className="text-left p-4">Blurb</th><th className="text-right p-4">Order</th>
              <th className="text-center p-4">Active</th><th className="text-right p-4 pr-6">Actions</th>
            </tr>
          </thead>
          <tbody>
            {categories.map((c) => (
              <tr key={c.slug} className="border-t border-neutral-100">
                <td className="p-4 font-medium">{c.name}</td>
                <td className="p-4 text-xs font-mono text-neutral-500">{c.slug}</td>
                <td className="p-4 text-xs text-neutral-500">{c.blurb}</td>
                <td className="p-4 text-right">{c.order}</td>
                <td className="p-4 text-center"><span className={`inline-block w-2 h-2 rounded-full ${c.active ? "bg-green-500" : "bg-neutral-300"}`} /></td>
                <td className="p-4 pr-6 text-right">
                  <div className="inline-flex gap-2">
                    <button onClick={() => openEdit(c)} className="text-xs font-semibold text-[#FF5A1F] hover:underline flex items-center gap-1"><Edit3 className="w-3.5 h-3.5" /> Edit</button>
                    <button onClick={() => del(c)} className="text-xs font-semibold text-red-600 hover:underline flex items-center gap-1"><Trash2 className="w-3.5 h-3.5" /> Delete</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {(creating || editing) && (
        <Modal onClose={close} title={creating ? "Add Category" : "Edit Category"}>
          <Field label="Name *"><input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="input" data-testid="cf-name" /></Field>
          <Field label="Slug (URL) *">
            <input value={form.slug} disabled={!creating} onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase().replace(/\s+/g, "-") })} className="input disabled:bg-neutral-100" data-testid="cf-slug" />
          </Field>
          <Field label="Blurb"><input value={form.blurb} onChange={(e) => setForm({ ...form, blurb: e.target.value })} className="input" placeholder="Short tagline shown under category name" /></Field>
          <Field label="Image URL"><input value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} className="input font-mono text-xs" /></Field>
          <Field label="Sort order (lower = first)"><input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} className="input" /></Field>
          <label className="flex items-center gap-2 mt-3">
            <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="accent-[#FF5A1F] w-4 h-4" />
            <span className="text-sm">Active</span>
          </label>
          <div className="mt-6 flex gap-2 justify-end">
            <button onClick={close} className="btn-secondary">Cancel</button>
            <button onClick={save} className="btn-primary" data-testid="save-category-btn">{creating ? "Create" : "Update"}</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ============ Banners ============
const emptyBanner = { slug: "", location: "hero", title: "", subtitle: "", eyebrow: "", cta_text: "", cta_link: "", image: "", image_secondary: "", order: 100, active: true };

function BannersTab({ banners, api, onChange }) {
  const { token } = useAdmin();
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(emptyBanner);

  const openCreate = () => { setForm(emptyBanner); setCreating(true); setEditing(null); };
  const openEdit = (b) => { setForm({ ...b }); setEditing(b.slug); setCreating(false); };
  const close = () => { setCreating(false); setEditing(null); };

  const save = async () => {
    try {
      if (creating) { await api.post("/admin/banners", { ...form, order: Number(form.order) || 100 }); toast.success("Banner created"); }
      else { const { slug: _slug, ...upd } = form; await api.patch(`/admin/banners/${editing}`, { ...upd, order: Number(upd.order) || 100 }); toast.success("Banner updated"); }
      close(); onChange();
    } catch (e) { toast.error("Save failed", { description: e?.response?.data?.detail || e.message }); }
  };
  const del = async (b) => {
    if (!window.confirm(`Delete banner "${b.title}"?`)) return;
    try { await api.delete(`/admin/banners/${b.slug}`); toast.success("Deleted"); onChange(); }
    catch (e) { toast.error("Delete failed", { description: e?.response?.data?.detail || e.message }); }
  };

  return (
    <div data-testid="admin-banners-tab">
      <div className="flex justify-between items-center mb-4">
        <div className="text-sm text-neutral-500">{banners.length} banner{banners.length !== 1 ? "s" : ""}</div>
        <button onClick={openCreate} className="btn-primary flex items-center gap-2" data-testid="add-banner-btn">
          <Plus className="w-4 h-4" /> Add Banner
        </button>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        {banners.map((b) => (
          <div key={b.slug} className="bg-white border border-neutral-200 rounded-2xl overflow-hidden">
            <div className="aspect-[16/8] bg-neutral-100 flex items-center justify-center">
              {b.image ? <img src={b.image} alt={b.title} className="max-w-full max-h-full object-contain" /> : <ImageIcon className="w-8 h-8 text-neutral-400" />}
            </div>
            <div className="p-5">
              <div className="flex items-center justify-between gap-2">
                <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-neutral-900 text-white">{b.location}</span>
                <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${b.active ? "bg-green-100 text-green-800" : "bg-neutral-100 text-neutral-600"}`}>
                  {b.active ? "Active" : "Hidden"}
                </span>
              </div>
              <h3 className="mt-2 font-display text-lg font-black line-clamp-1">{b.title}</h3>
              {b.subtitle && <p className="text-xs text-neutral-500 line-clamp-2 mt-1">{b.subtitle}</p>}
              <div className="mt-3 flex gap-2 justify-end">
                <button onClick={() => openEdit(b)} className="text-xs font-semibold text-[#FF5A1F] hover:underline flex items-center gap-1"><Edit3 className="w-3.5 h-3.5" /> Edit</button>
                <button onClick={() => del(b)} className="text-xs font-semibold text-red-600 hover:underline flex items-center gap-1"><Trash2 className="w-3.5 h-3.5" /> Delete</button>
              </div>
            </div>
          </div>
        ))}
        {banners.length === 0 && <div className="text-neutral-500 col-span-2 text-center py-10">No banners yet.</div>}
      </div>

      {(creating || editing) && (
        <Modal onClose={close} title={creating ? "Add Banner" : "Edit Banner"}>
          <div className="grid md:grid-cols-2 gap-3">
            <Field label="Slug *">
              <input value={form.slug} disabled={!creating} onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase().replace(/\s+/g, "-") })} className="input disabled:bg-neutral-100" data-testid="bf-slug" />
            </Field>
            <Field label="Location *">
              <select value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="input" data-testid="bf-location">
                {LOCATIONS.map((l) => <option key={l} value={l}>{l}</option>)}
              </select>
            </Field>
          </div>
          <Field label="Eyebrow (small label above title)"><input value={form.eyebrow} onChange={(e) => setForm({ ...form, eyebrow: e.target.value })} placeholder="e.g. New Drop, Limited Time" className="input" /></Field>
          <Field label="Title *"><input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="input" data-testid="bf-title" /></Field>
          <Field label="Subtitle"><textarea value={form.subtitle} onChange={(e) => setForm({ ...form, subtitle: e.target.value })} rows={2} className="input resize-none" /></Field>
          <div className="grid md:grid-cols-2 gap-3">
            <Field label="CTA text"><input value={form.cta_text} onChange={(e) => setForm({ ...form, cta_text: e.target.value })} placeholder="Shop the Collection" className="input" /></Field>
            <Field label="CTA link"><input value={form.cta_link} onChange={(e) => setForm({ ...form, cta_link: e.target.value })} placeholder="/shop" className="input" /></Field>
          </div>
          <Field label="Image URL">
            <input value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} className="input font-mono text-xs" />
            <div className="mt-2"><ImageUploader token={token} onUploaded={(url) => setForm({ ...form, image: url })} /></div>
          </Field>
          <Field label="Secondary image URL (for hero only)">
            <input value={form.image_secondary} onChange={(e) => setForm({ ...form, image_secondary: e.target.value })} className="input font-mono text-xs" />
            <div className="mt-2"><ImageUploader token={token} onUploaded={(url) => setForm({ ...form, image_secondary: url })} /></div>
          </Field>
          <Field label="Sort order (lower = first)"><input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} className="input" /></Field>
          <label className="flex items-center gap-2 mt-3">
            <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="accent-[#FF5A1F] w-4 h-4" />
            <span className="text-sm">Active</span>
          </label>
          <div className="mt-6 flex gap-2 justify-end">
            <button onClick={close} className="btn-secondary">Cancel</button>
            <button onClick={save} className="btn-primary" data-testid="save-banner-btn">{creating ? "Create" : "Update"}</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ============ shared UI ============
function Modal({ title, children, onClose, wide }) {
  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className={`relative bg-white rounded-3xl p-6 md:p-8 w-full ${wide ? "max-w-3xl" : "max-w-lg"} max-h-[90vh] overflow-auto`}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-2xl font-black">{title}</h3>
          <button onClick={onClose} className="p-1 text-neutral-500 hover:text-neutral-900"><X className="w-5 h-5" /></button>
        </div>
        <style>{`.input { width: 100%; height: 42px; border: 1px solid #d4d4d4; border-radius: 12px; padding: 0 12px; font-size: 14px; background: white; }
                 .input:focus { outline: none; border-color: #171717; }
                 textarea.input { padding: 10px 12px; height: auto; }
                 .btn-primary { height: 40px; padding: 0 20px; border-radius: 999px; background: #FF5A1F; color: white; font-weight: 700; font-size: 13px; }
                 .btn-primary:hover { background: #171717; }
                 .btn-primary:disabled { opacity: 0.6; }
                 .btn-secondary { height: 40px; padding: 0 20px; border-radius: 999px; border: 1px solid #d4d4d4; font-weight: 600; font-size: 13px; }`}</style>
        {children}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="mt-3">
      <div className="text-xs font-semibold uppercase tracking-[0.14em] text-neutral-600 mb-1.5">{label}</div>
      {children}
    </div>
  );
}
